import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { FaSearch, FaUsers, FaUser, FaPhone, FaMapMarkerAlt, FaEye, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const PAGE_SIZE = 30; // items per page (reduced from 30 to show pagination sooner)

function CustomerPage() {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getData = async (page = 1, search = '') => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/userdata?page=${page}&limit=${PAGE_SIZE}&search=${search}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const users = response.data.users || [];
            setCustomers(users);
            setFilteredCustomers(users);
            setCurrentPage(response.data.currentPage);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    // Search customers
    const handleSearchCustomer = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = customers.filter(customer => {
                const firstAddress = customer.address?.[0] || {};
                return (
                    firstAddress.name?.toLowerCase().includes(search) ||
                    firstAddress.mobile?.includes(search) ||
                    formatAddress(customer.address).toLowerCase().includes(search)
                );
            });
            setFilteredCustomers(filtered);
        } else {
            setFilteredCustomers(customers);
        }
    };

    const handleViewDetails = (customer) => {
        setSelectedCustomer(customer);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedCustomer(null);
    };

    useEffect(() => {
        getData();
    }, []);

    // Close modal on ESC & lock scroll when open
    useEffect(() => {
        if (!showModal) return;
        const keyHandler = (e) => { if (e.key === 'Escape') closeModal(); };
        document.addEventListener('keydown', keyHandler);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', keyHandler);
            document.body.style.overflow = prev;
        };
    }, [showModal]);

    // Formats the address array correctly
    const formatAddress = (addressArray) => {
        if (!addressArray || addressArray.length === 0) {
            return "No Address";
        }
        return addressArray.map(addr =>
            `${addr.house || "N/A"}, ${addr.area || "N/A"}, ${addr.city || "N/A"}, ${addr.landmark ? `Landmark: ${addr.landmark}` : ""}`
        ).join("; ");
    };

    const CustomerRow = ({ customer, index }) => {
        const firstAddress = customer.address?.[0] || {};
        return (
            <tr>
                <td className="fw-semibold text-muted">{index + 1 + (currentPage - 1) * PAGE_SIZE}</td>
                <td>
                    <div className="fw-bold text-dark" style={{ lineHeight: 1.3 }}>{firstAddress.name || "N/A"}</div>
                    <small className="text-muted">ID: {customer._id?.substring(0, 8)}...</small>
                </td>
                <td>
                    <div className="d-flex align-items-center gap-2">
                        <FaPhone className="text-muted" style={{ fontSize: '12px' }} />
                        <span className="fw-semibold">{firstAddress.mobile || "N/A"}</span>
                    </div>
                </td>
                <td>
                    <div className="d-flex align-items-start gap-2">
                        <FaMapMarkerAlt className="text-muted mt-1 flex-shrink-0" style={{ fontSize: '12px' }} />
                        <small className="text-muted">{formatAddress(customer.address)}</small>
                    </div>
                </td>
                <td className="text-end pe-4">
                    <div className="order-actions-wrapper justify-content-end">
                        <button
                            type="button"
                            className="order-action-btn order-action-btn-view"
                            title="View Details"
                            onClick={() => handleViewDetails(customer)}
                        >
                            <FaEye />
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    // Build compact page number array (first, last, current±1 with ellipsis)
    const buildPageList = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            const first = 1, last = totalPages;
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            pages.push(first);
            if (start > 2) pages.push('ellipsis-start');
            for (let p = start; p <= end; p++) pages.push(p);
            if (end < totalPages - 1) pages.push('ellipsis-end');
            pages.push(last);
        }
        return pages;
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1">USER MANAGEMENT</h4>
                            <p className="text-muted p-0 m-0">Manage customer accounts and information</p>
                        </div>
                    </div>
                </div>

                <div className="pending-control-panel">
                    <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px' }}>
                        <input
                            type="text"
                            className="form-control search-input-custom w-100"
                            placeholder="Search by name, mobile, or address..."
                            value={searchTerm}
                            onChange={handleSearchCustomer}
                        />
                        <FaSearch className="search-icon-custom" />
                    </div>
                    <div className="d-flex align-items-center">
                        <small className="text-muted fw-semibold">
                            Showing {filteredCustomers.length} of {customers.length} customers
                        </small>
                    </div>
                </div>

                {loading ? (
                    <LoadingScreen />
                ) : (
                    <div className="row mt-0">
                        <div className="col-12">
                            {filteredCustomers.length > 0 ? (
                                <div className="card order-table-card">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>CUSTOMER</th>
                                                    <th>CONTACT</th>
                                                    <th>ADDRESS</th>
                                                    <th className="text-end pe-4">ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredCustomers.map((customer, index) => (
                                                    <CustomerRow key={customer._id} customer={customer} index={index} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="card order-table-card">
                                    <div className="card-body text-center py-5">
                                        <FaUsers className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No customers found matching your search.' : 'No customers found'}
                                        </h5>
                                        <p className="text-muted mb-0">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Customer data will appear here.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {!loading && filteredCustomers.length > 0 && !searchTerm && totalPages > 1 && (
                    <div className="row mt-4">
                        <div className="col-12 d-flex justify-content-center">
                            <nav className="customer-pagination" aria-label="Customer pages">
                                <button
                                    type="button"
                                    className="page-btn nav-btn prev"
                                    disabled={currentPage === 1}
                                    onClick={() => currentPage > 1 && getData(currentPage - 1)}
                                    aria-label="Previous page"
                                >
                                    <FaChevronLeft />
                                </button>
                                {buildPageList().map((p, i) => (
                                    p === 'ellipsis-start' || p === 'ellipsis-end' ? (
                                        <span key={p + i} className="page-ellipsis">...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            type="button"
                                            className={`page-btn number ${p === currentPage ? 'active' : ''}`}
                                            aria-current={p === currentPage ? 'page' : undefined}
                                            onClick={() => p !== currentPage && getData(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                ))}
                                <button
                                    type="button"
                                    className="page-btn nav-btn next"
                                    disabled={currentPage === totalPages}
                                    onClick={() => currentPage < totalPages && getData(currentPage + 1)}
                                    aria-label="Next page"
                                >
                                    <FaChevronRight />
                                </button>
                                <span className="page-info">Page {currentPage} of {totalPages}</span>
                            </nav>
                        </div>
                    </div>
                )}
            </div>

            {/* Customer Details Modal */}
            {showModal && selectedCustomer && (
                <>
                    <div className="custom-modal-backdrop" />
                    <div className="modal fade show customer-details-modal" style={{ display: 'block' }} tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content customer-details-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                                        <FaUser className="text-danger" />
                                        Customer Details
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                        aria-label="Close details modal"
                                    ></button>
                                </div>
                                <div className="modal-body pt-0">
                                    <div className="customer-meta-grid">
                                        <div className="meta-block">
                                            <span className="meta-label">Customer ID</span>
                                            <span className="meta-value mono">{selectedCustomer._id}</span>
                                        </div>
                                        <div className="meta-block">
                                            <span className="meta-label">Name</span>
                                            <span className="meta-value">{selectedCustomer.address?.[0]?.name || 'N/A'}</span>
                                        </div>
                                        <div className="meta-block">
                                            <span className="meta-label">Mobile</span>
                                            <span className="meta-value d-flex align-items-center gap-2">
                                                <FaPhone className="text-muted" />
                                                {selectedCustomer.address?.[0]?.mobile || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                    {selectedCustomer.address && selectedCustomer.address.length > 0 ? (
                                        selectedCustomer.address.map((addr, index) => (
                                            <div key={index} className="address-card">
                                                <div className="address-head">Address {index + 1}</div>
                                                <div className="address-line d-flex align-items-start gap-2">
                                                    <FaMapMarkerAlt className="text-muted mt-1" />
                                                    <div>
                                                        <div>{addr.house || 'N/A'}, {addr.area || 'N/A'}</div>
                                                        <div>{addr.city || 'N/A'}</div>
                                                        {addr.landmark && <div className="text-muted"><small>Landmark: {addr.landmark}</small></div>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="address-card first-child">
                                            <p className="text-muted mb-0">No addresses found</p>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer pt-0">
                                    <button
                                        type="button"
                                        className="btn btn-light rounded-0 border"
                                        onClick={closeModal}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className='mt-5 mb-5 pb-5'></div>
                    </div>
                </>
            )}
        </Fragment>
    );
}

export default CustomerPage;
